import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme, App as AntApp } from 'antd';
import useThemeStore from './store/themeStore';
import useAuthStore from './store/authStore';

// Layouts
import AuthLayout from './components/Auth/AuthLayout';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Pages
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Auth/RegisterForm';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';

// Main Pages
import Dashboard from './pages/Dashboard';
import LeadsPage from './pages/LeadsPage';
import DealsPage from './pages/DealsPage';
import MeetingsPage from './pages/MeetingsPage';
import TasksPage from './pages/TasksPage';
import TaskDetailView from './pages/TaskDetailView';
import AnalyticsPage from './pages/AnalyticsPage';
import ReportsPage from './pages/ReportsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AIAgentPage from './pages/AIAgentPage';

// Admin Pages
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';



// Error Pages
import NotFoundPage from './pages/NotFoundPage';

function App() {
  const { isDark } = useThemeStore();
  const { isAuthenticated } = useAuthStore();

  const antdTheme = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: '#0ea5e9',
      borderRadius: 8,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    components: {
      Layout: {
        headerBg: isDark ? '#1f2937' : '#ffffff',
        siderBg: isDark ? '#1f2937' : '#ffffff',
        bodyBg: isDark ? '#111827' : '#f9fafb',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: isDark ? '#374151' : '#e0f2fe',
        itemHoverBg: isDark ? '#374151' : '#f0f9ff',
      },
      Button: {
        borderRadius: 8,
        controlHeight: 40,
      },
      Input: {
        borderRadius: 8,
        controlHeight: 40,
      },
      Card: {
        borderRadius: 12,
      },
    },
  };

  

  

  return (
    <ConfigProvider theme={antdTheme}>
      <AntApp>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/auth" element={<AuthLayout />}>
                <Route 
                  path="login" 
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />
                  } 
                />
                <Route 
                  path="register" 
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterForm />
                  } 
                />
              </Route>

              {/* Password Reset Routes (outside AuthLayout for full-width) */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* Legacy auth routes (redirect to new structure) */}
              <Route path="/login" element={<Navigate to="/auth/login" replace />} />
              <Route path="/register" element={<Navigate to="/auth/register" replace />} />

              {/* Protected Routes */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* Dashboard */}
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="ai-agent" element={<AIAgentPage />} />

                {/* CRM Routes */}
                <Route path="leads/*" element={<LeadsPage />} />
                <Route path="deals/*" element={<DealsPage />} />
                <Route path="meetings" element={<MeetingsPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="tasks/:id" element={<TaskDetailView />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="reports" element={<ReportsPage />} />

                {/* User Routes */}
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<SettingsPage />} />

                {/* Admin Routes */}
                <Route 
                  path="admin/users" 
                  element={
                    <ProtectedRoute requiredRoles={['super_admin', 'tenant_admin']}>
                      <AdminUsersPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="admin/settings" 
                  element={
                    <ProtectedRoute requiredRoles={['super_admin', 'tenant_admin']}>
                      <AdminSettingsPage />
                    </ProtectedRoute>
                  } 
                />
              </Route>

              {/* 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </Router>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
